import type { TFunction } from 'i18next';
import {
  Briefcase,
  Building2,
  Calendar,
  ChevronLeft,
  MapPin,
  Star,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { ListHomeCareVendorsForPatients } from '@virtual-care/client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
} from '@virtual-care/shadcn';

type VendorCardProps = {
  vendor: ListHomeCareVendorsForPatients['records'][number];
};

const WEEKDAY_ORDER = [
  'SATURDAY',
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
];

function formatAvailabilityRange(days: string[], t: TFunction): string {
  if (days.length === 0) return '';
  if (days.length === 7) return t('homecare.serviceListing.availableDaily');

  const weekdayName = (key: string) =>
    t(`homecare.serviceListing.weekdays.${key}`);

  const sortedDays = [...days].sort(
    (a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b),
  );

  const indices = sortedDays.map((d) => WEEKDAY_ORDER.indexOf(d));
  const isConsecutive =
    sortedDays.length >= 2 &&
    indices.every((idx, i) => i === 0 || idx - indices[i - 1] === 1);

  if (isConsecutive) {
    return `${weekdayName(sortedDays[0])} - ${weekdayName(sortedDays[sortedDays.length - 1])}`;
  }

  return sortedDays.map(weekdayName).join(t('common.listSeparator'));
}

export function VendorCard({ vendor }: VendorCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isIndividual = vendor.vendorType === 'INDIVIDUAL';

  const vendorName = isIndividual
    ? `${vendor.individualProfile?.firstName ?? ''} ${vendor.individualProfile?.lastName ?? ''}`
    : (vendor.clinicProfile?.clinicName ?? '');

  const cityName = isIndividual
    ? vendor.individualProfile?.cityName
    : vendor.clinicProfile?.cityName;

  const yearsOfExperience = isIndividual
    ? vendor.individualProfile?.yearsOfExperience
    : null;

  const avatarInitial = isIndividual
    ? vendor.individualProfile?.firstName?.[0]
    : vendor.clinicProfile?.clinicName?.[0];

  const logoUrl = vendor.clinicProfile?.logoFile?.path;

  const serviceNames = vendor.services
    .map((s) => s.name)
    .join(t('common.listSeparator'));

  const availabilityText = formatAvailabilityRange(vendor.availabilityDays, t);

  const handleBookNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`vendor/${vendor.id}/book`);
  };

  return (
    <Card
      className="hover:border-primary cursor-pointer border-gray-200 py-0 transition-all duration-200 hover:shadow-lg"
      onClick={() => navigate(`vendor/${vendor.id}/book`)}
    >
      <CardContent className="p-4">
        <div className="mb-3 flex items-start gap-3">
          <Avatar className="size-10 shrink-0">
            {logoUrl && <AvatarImage src={logoUrl} alt={vendorName} />}
            <AvatarFallback
              className={
                isIndividual
                  ? 'bg-blue-100 text-lg text-blue-700'
                  : 'bg-purple-100 text-lg text-purple-700'
              }
            >
              {avatarInitial}
            </AvatarFallback>
          </Avatar>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="truncate text-base font-bold text-gray-900">
                {vendorName}
              </h3>
              <Badge
                variant="secondary"
                className={
                  isIndividual
                    ? 'shrink-0 bg-blue-50 text-blue-700 hover:bg-blue-50'
                    : 'shrink-0 bg-purple-50 text-purple-700 hover:bg-purple-50'
                }
              >
                {isIndividual ? (
                  <User className="me-1 h-3 w-3" />
                ) : (
                  <Building2 className="me-1 h-3 w-3" />
                )}
                {isIndividual
                  ? t('homecare.serviceListing.vendorTypeIndividual')
                  : t('homecare.serviceListing.vendorTypeClinic')}
              </Badge>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-500">
              {cityName && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {cityName}
                </span>
              )}
              {yearsOfExperience && (
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5" />
                  {t('homecare.serviceListing.yearsExperience', {
                    value: yearsOfExperience,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>

        {serviceNames && (
          <div className="mb-2 flex items-start gap-2 text-sm text-gray-600">
            <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <span className="line-clamp-2">{serviceNames}</span>
          </div>
        )}

        {availabilityText && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
            <span>
              {t('homecare.serviceListing.availableLabel')} {availabilityText}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-gray-100 pt-3">
          <div className="flex items-baseline gap-1">
            <span className="text-sm text-gray-500">
              {t('homecare.serviceListing.priceFrom')}
            </span>
            <span className="text-xl font-bold text-green-600">
              {parseFloat(vendor.minPrice).toFixed(0)}
            </span>
            <span className="text-sm text-gray-500">
              {t('homecare.serviceListing.priceCurrency')}
            </span>
          </div>

          <Button
            size="sm"
            onClick={handleBookNow}
            className="bg-primary hover:bg-primary/90 gap-1"
          >
            {t('homecare.serviceListing.bookNow')}
            <ChevronLeft className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
